/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createContext, useMemo } from"react";
import { cn } from"@/shared/ui/utils/tailwind";

export type CardProps = {
  variant?:"paper" |"invert" |"none" |"outlined";
  size?:"nano"|"unset" |"micro" |"small" |"medium" |"large";
  addOutline?: boolean;
  removeShadow?: boolean;
  direction?:"horizontal" |"vertical";
  paddingless?: boolean;
  color?: string;
  className?: string;
  sx?: any;
  style?: React.CSSProperties;
  classNames?: any;
  as?: unknown;
  forwardRef?: React.RefObject<HTMLElement> | null;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onChange?: (event: React.FormEvent<HTMLInputElement>) => void;
};

const NON_DOM_PROPS = new Set([
  'as','component','forwardRef','sx','variant','size','addOutline',
  'removeShadow','direction','paddingless','color','classNames',
]);

function filterDomProps(props: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (!NON_DOM_PROPS.has(key)) result[key] = props[key];
  }
  return result;
}

const Card: ComponentWithChildren<CardProps> = ({
  variant ="paper",
  color ="primary",
  direction ="horizontal",
  size ="medium",
  addOutline = false,
  removeShadow = false,
  paddingless = false,
  children,
  sx,
  classNames,
  className,
  as: _as,
  forwardRef: _forwardRef,
  onChange: _onChange,
  onClick,
  ...props
}) => {
  void _as; void _forwardRef; void _onChange;
  const paddingClasses = paddingless
    ?""
    : size ==='large'
      ?"p-8"
      : size ==='medium'
        ?"p-4"
        : size ==='small'
          ?"p-2"
          :"";

  return (
    <div
      className={cn(
        "flex rounded-(--radius-lg) text-(--text-primary)",
        direction ==="vertical" ?"flex-col" :"flex-row",
        !removeShadow &&"shadow-sm",
        (variant ==='outlined' || addOutline) &&"border border-[var(--border-medium)]",
        variant ==='invert' &&"bg-[var(--surface-secondary)]",
        paddingClasses,
        className
      )}
      style={sx || undefined}
      onClick={onClick}
      {...(filterDomProps(props) as React.HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  );
};

/**
 * cardSizeToSx function.
 *
 * @param {*} size - Parameter description
 *
 * @returns {*} Return value description
 */
export function cardSizeToSx(size: CardProps["size"]): any {
  if (size === undefined) return {};
  const sizes: Record<string, any> = {
    unset: { padding: 0 },
    micro: { padding:'4px' },
    small: { padding:'8px' },
    medium: { padding:'16px' },
    large: { padding:'32px' },
  };

  return sizes[size] || {};
}

export const CardWithSectionsContext = createContext<Partial<CardProps>>({});
export const CardWithSections: ComponentWithChildren<CardProps> = ({
  size ="medium",
  direction,
  children,
  sx,
  ...props
}) => {
  const contextProps = useMemo(() => ({ size, direction }), [size, direction]);
  return (
    <CardWithSectionsContext.Provider value={contextProps}>
      <Card paddingless={true} direction="vertical" sx={sx} {...props}>
        {children}
      </Card>
    </CardWithSectionsContext.Provider>
  );
};

export default Card;
